import { type ComponentProps, useCallback, useEffect } from 'react';
import Animated, {
  Easing,
  type EasingFunction,
  ReduceMotion,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';
import { theme } from '@garnish/constants';

// ─────────────────────────────────────────────────────────────────────────────

export const FadeIn = (props: Props) => {
  const {
    delay = DEFAULT_ANIMATION_DELAY,
    duration = DEFAULT_ANIMATION_DURATION,
    easing = DEFAULT_EASING,
    style,
    children,
    ...restProps
  } = props;

  // ─── Styles ──────────────────────────────────────────────────────────

  const opacitySV = useSharedValue(0);

  const animatedStyles = useAnimatedStyle(() => {
    return { opacity: opacitySV.value };
  });

  // ─── Helpers ─────────────────────────────────────────────────────────

  const fadeIn = useCallback(() => {
    opacitySV.value = withDelay(
      delay,
      withTiming(1, { easing, duration, reduceMotion: ReduceMotion.System }),
    );
  }, [opacitySV, delay, duration, easing]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(fadeIn, [fadeIn]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View {...restProps} style={[animatedStyles, style]}>
      {children}
    </Animated.View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_EASING = Easing.inOut(Easing.ease);
const DEFAULT_ANIMATION_DELAY = 0;
const DEFAULT_ANIMATION_DURATION = theme.transitions.long;

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  delay?: number;
  duration?: number;
  easing?: EasingFunction;
} & ComponentProps<typeof Animated.View>;
